// V5 Synthesis Telemetry Panel
// Displays token budget, complexity profile, pipeline health, and gate check status
// Reads from the synthesis status polling response

import React from 'react';
import { Shield, Zap, AlertTriangle, CheckCircle, Activity, Cpu, DollarSign } from 'lucide-react';

interface TokenSummary {
  total_tokens: number;
  ceiling: number;
  estimated_cost_usd: number;
  per_agent: Record<string, number>;
  halted: boolean;
}

interface ComplexityProfile {
  complexity_level: string;
  bom_target: string;
  fmea_chunk: number;
  mc_samples: number;
  label?: string;
}

interface PipelineFailure {
  agent: string;
  layer: string;
  error: string;
  impact: string;
}

interface SynthesisTelemetryProps {
  tokenSummary?: TokenSummary;
  complexityProfile?: ComplexityProfile;
  pipelineFailures?: PipelineFailure[];
}

const COMPLEXITY_COLORS: Record<string, string> = {
  LOW: '#22c55e',
  MEDIUM: '#f59e0b',
  HIGH: '#ef4444',
};

const COMPLEXITY_ICONS: Record<string, string> = {
  LOW: '🟢',
  MEDIUM: '🟡',
  HIGH: '🔴',
};

export const SynthesisTelemetry: React.FC<SynthesisTelemetryProps> = ({
  tokenSummary,
  complexityProfile,
  pipelineFailures,
}) => {
  if (!tokenSummary && !complexityProfile && !pipelineFailures?.length) {
    return null; // Don't render if no V5 data is available
  }

  const tokenPct = tokenSummary
    ? Math.min(100, Math.round((tokenSummary.total_tokens / tokenSummary.ceiling) * 100))
    : 0;

  const barColor =
    tokenPct > 90 ? '#ef4444' : tokenPct > 70 ? '#f59e0b' : '#22c55e';

  const gateFails = (pipelineFailures || []).filter((f) => f.agent === 'SipocGateService');
  const agentFails = (pipelineFailures || []).filter((f) => f.agent !== 'SipocGateService');

  return (
    <div
      style={{
        background: 'rgba(255,255,255,0.06)',
        backdropFilter: 'blur(12px)',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: '12px',
        padding: '16px',
        marginBottom: '12px',
        fontFamily: "'Inter', sans-serif",
        color: '#e2e8f0',
        fontSize: '13px',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
        <Shield size={16} color="#818cf8" />
        <span style={{ fontWeight: 600, fontSize: '14px', color: '#c7d2fe' }}>
          SIPOC V5 Telemetry
        </span>
      </div>

      {/* Row: Complexity + Token Budget */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
        {/* Complexity Badge */}
        {complexityProfile && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 12px',
              background: 'rgba(0,0,0,0.2)',
              borderRadius: '8px',
              border: `1px solid ${COMPLEXITY_COLORS[complexityProfile.complexity_level] || '#6366f1'}33`,
            }}
          >
            <Cpu size={14} color={COMPLEXITY_COLORS[complexityProfile.complexity_level] || '#818cf8'} />
            <div>
              <div style={{ fontSize: '11px', opacity: 0.6 }}>Complexity</div>
              <div style={{ fontWeight: 600, color: COMPLEXITY_COLORS[complexityProfile.complexity_level] || '#c7d2fe' }}>
                {COMPLEXITY_ICONS[complexityProfile.complexity_level] || ''}{' '}
                {complexityProfile.complexity_level}
              </div>
            </div>
            <div style={{ marginLeft: 'auto', textAlign: 'right', fontSize: '11px', opacity: 0.5 }}>
              BOM: {complexityProfile.bom_target}
              <br />
              MC: {complexityProfile.mc_samples?.toLocaleString()}
            </div>
          </div>
        )}

        {/* Token Budget */}
        {tokenSummary && (
          <div
            style={{
              padding: '8px 12px',
              background: 'rgba(0,0,0,0.2)',
              borderRadius: '8px',
              border: tokenSummary.halted
                ? '1px solid rgba(239,68,68,0.5)'
                : '1px solid rgba(255,255,255,0.08)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
              <DollarSign size={14} color={tokenSummary.halted ? '#ef4444' : '#22c55e'} />
              <span style={{ fontSize: '11px', opacity: 0.6 }}>Token Budget</span>
              <span
                style={{
                  marginLeft: 'auto',
                  fontSize: '11px',
                  fontWeight: 600,
                  color: barColor,
                }}
              >
                {tokenPct}%
              </span>
            </div>
            {/* Progress bar */}
            <div
              style={{
                height: '4px',
                background: 'rgba(255,255,255,0.08)',
                borderRadius: '2px',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${tokenPct}%`,
                  height: '100%',
                  background: barColor,
                  borderRadius: '2px',
                  transition: 'width 0.3s ease',
                }}
              />
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: '4px',
                fontSize: '10px',
                opacity: 0.5,
              }}
            >
              <span>{(tokenSummary.total_tokens / 1000).toFixed(0)}K tokens</span>
              <span>~${tokenSummary.estimated_cost_usd?.toFixed(3)}</span>
            </div>
            {tokenSummary.halted && (
              <div
                style={{
                  marginTop: '4px',
                  fontSize: '11px',
                  color: '#ef4444',
                  fontWeight: 600,
                }}
              >
                ⚠ BUDGET HALTED — circuit breaker triggered
              </div>
            )}
          </div>
        )}
      </div>

      {/* Gate Check Results */}
      {gateFails.length > 0 && (
        <div style={{ marginBottom: '8px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              marginBottom: '4px',
              fontSize: '11px',
              fontWeight: 600,
              color: '#fbbf24',
            }}
          >
            <AlertTriangle size={12} />
            Gate Issues ({gateFails.length})
          </div>
          {gateFails.map((f, i) => (
            <div
              key={i}
              style={{
                fontSize: '11px',
                padding: '4px 8px',
                background: 'rgba(251,191,36,0.08)',
                borderRadius: '4px',
                marginBottom: '2px',
                borderLeft: '2px solid #fbbf2444',
              }}
            >
              <span style={{ fontWeight: 500 }}>{f.layer}</span>: {f.error.replace('GATE_', '')}
            </div>
          ))}
        </div>
      )}

      {/* Agent Failures */}
      {agentFails.length > 0 && (
        <div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              marginBottom: '4px',
              fontSize: '11px',
              fontWeight: 600,
              color: '#f87171',
            }}
          >
            <Activity size={12} />
            Pipeline Issues ({agentFails.length})
          </div>
          {agentFails.slice(-5).map((f, i) => (
            <div
              key={i}
              style={{
                fontSize: '11px',
                padding: '4px 8px',
                background: 'rgba(248,113,113,0.06)',
                borderRadius: '4px',
                marginBottom: '2px',
                borderLeft: '2px solid #f8717144',
              }}
            >
              <span style={{ fontWeight: 500 }}>[{f.layer}] {f.agent}</span>:{' '}
              {f.error.length > 80 ? f.error.substring(0, 80) + '…' : f.error}
            </div>
          ))}
        </div>
      )}

      {/* All clear */}
      {!gateFails.length && !agentFails.length && tokenSummary && !tokenSummary.halted && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '11px',
            color: '#4ade80',
          }}
        >
          <CheckCircle size={12} />
          All gates passed • Pipeline healthy
        </div>
      )}
    </div>
  );
};
